import React from "react";
import { DateRange } from "react-date-range";
import {
    Popper,
    ClickAwayListener,
    InputAdornment,
} from "@mui/material";
import { CustomTextField } from "./CustomTextField";
import {CloseModalIcon, OutlinedCalendarIcon} from "./buttons/IconButtons";
import "react-date-range/dist/styles.css";
import "react-date-range/dist/theme/default.css";
import { colors } from '../enums/colors';
import ButtonSubAction from "./buttons/ButtonSubAction";
import CustomDateRangePickerApp from "./CustomDateRangePicker/CustomDateRangePickerApp";
import {CustomDateRangePickerStyle} from "./Styles";
import {dateInputLanguage} from "../hooks/dateInputLanguage";

/**
 * @Author: Karen Yunuen Pozos Arias
 * @JobTitle : Full Stack Developer - IBL
 * @Email : kpozos@ibl.mx
 *
 * A custom wrapper around `react-date-range` that displays a styled
 * date range picker inside a MUI `Popper`. It allows the user to select
 * a start and end date and confirm/cancel the selection.
 *
 * @component
 * @param {string} props.id - Unique identifier for the input and popper.
 * @param {boolean} [props.disabled=false] - Disables the input field if true.
 * @param {Function} props.trans - Translation function for labels.
 * @param {boolean} [props.required=false] - Whether the input is required.
 * @param {string} [props.helperText] - Optional helper text below the input.
 * @param {boolean} [props.isValid=true] - Validation state for the input.
 * @param {string} [props.isValidText="date_invalid"] - Message when the input is invalid.
 * @param {boolean} props.endDateIndeterminate - Indicates whether it will be for intervals where start is a date and end is a variable that represents an undefined value.
 * @param {string} props.textIndeterminate - Mask text for the end variable input: for example, "forever".
 * @param {Date|undefined} [props.minDate] - Minimum selectable date for the calendar.
 * @param {Function} props.setRangeDate - Function to update the selected date range.
 * @param {Date} [props.maxDate=new Date()] - Maximum selectable date for the calendar. Defaults to the current date to prevent selecting future dates.
 * @param {number} props.rangeLength - Fixed amount of time to extend the range from the selected start date.
 * @param {string} props.rangeUnit - Unit of time for the range calculation. Used together with `rangeLength`
 *                                   to automatically determine the end date (e.g., 7 DAYS, 1 MONTH).
 */

export const CustomDateRangePicker = (props) => {
    const {
        id,
        disabled,
        trans,
        required = false,
        helperText,
        isValid = true,
        isValidText = "date_invalid",
        endDateIndeterminate = false,
        textIndeterminate = "forever",
        minDate = undefined,
        value,
        setRangeDate,
        maxDate = undefined,
        rangeLength,
        rangeUnit
    } = props;

    const {
        open,
        lCode,
        anchorRef,
        formattedValue,
        monthsToShow,
        tempRange,
        height,
        buttonHeight,
        calendarRef,
        focusedRange,
        setFocusedRange,
        handleCancel,
        handleApply,
        handleChange,
        handleClickAway,
        onFocus,
        getStyleDayContentRenderer
    } = CustomDateRangePickerApp({
        value,
        setRangeDate,
        endDateIndeterminate,
        textIndeterminate,
        rangeLength,
        rangeUnit
    });

    return (
        <ClickAwayListener onClickAway={handleClickAway}>
            <div>
                {/* Input field */}
                <div ref={anchorRef}>
                    <CustomTextField
                        id={id}
                        value={formattedValue}
                        disabled={disabled}
                        trans={trans}
                        type="text"
                        required={required}
                        onFocus={onFocus}
                        InputProps={{
                            endAdornment: (
                                <InputAdornment position="end" onClick={onFocus} className={"cursor-pointer"}>
                                    <OutlinedCalendarIcon />
                                </InputAdornment>
                            ),
                            readOnly: true,
                        }}
                        helperText={
                            !isValid && isValidText ? isValidText : helperText
                        }
                        showCounter={false}
                    />
                </div>

                {/* Popper with calendar */}
                <Popper
                    open={open}
                    anchorEl={anchorRef.current}
                    placement="bottom-start"
                    style={CustomDateRangePickerStyle.popper(monthsToShow === 2 ? "auto" : anchorRef.current
                        ? anchorRef.current.offsetWidth
                        : "auto")
                    }
                >
                    <div className="background-general-00 shadow-sm p-3" style={CustomDateRangePickerStyle.popperContainer(height)}>
                        <div className={"overflow-auto"} style={CustomDateRangePickerStyle.popperContainerScroll(height - buttonHeight)}>
                            <div className="d-flex justify-content-end">
                                <button
                                    id={`btn_close_${id}`}
                                    type="button"
                                    className="btn btn-icon-cancel p-0"
                                    onClick={handleCancel}
                                >
                                    <CloseModalIcon />
                                </button>
                            </div>

                            {/* Calendar */}
                            <DateRange
                                focusedRange={focusedRange}
                                onRangeFocusChange={(endDateIndeterminate || rangeLength) ? undefined : setFocusedRange}
                                ref={ref => {
                                    if (ref && ref.calendar) {
                                        calendarRef.current = ref.calendar;
                                    }
                                }}
                                ranges={tempRange}
                                onChange={handleChange}
                                moveRangeOnFirstSelection={!endDateIndeterminate}
                                months={endDateIndeterminate ? 1 : monthsToShow}
                                dragSelectionEnabled={!endDateIndeterminate}
                                retainEndDateOnFirstSelection={!endDateIndeterminate}
                                direction="horizontal"
                                showMonthAndYearPickers={false}
                                showDateDisplay={false}
                                locale={dateInputLanguage(lCode)}
                                color={colors.text50}
                                className="w-100"
                                dayContentRenderer={getStyleDayContentRenderer}
                                minDate={minDate}
                                maxDate={maxDate}
                            />
                        </div>
                        {/* Confirm button */}
                        <div className="d-flex justify-content-end">
                            <ButtonSubAction
                                id={`btn_confirm_${id}`}
                                name="confirm"
                                onClick={handleApply}
                            />
                        </div>
                    </div>
                </Popper>
            </div>
        </ClickAwayListener>
    );
};
